import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Loader2, ArrowRight, Shield, Eye, EyeOff, Smartphone } from 'lucide-react';
import { InstallAppButton } from '../components/InstallAppButton';
import { InstallBanner } from '../components/InstallBanner';

const CHINOOK_LOGO = 'https://customer-assets.emergentagent.com/job_66080daa-dc30-4cf8-b53e-8e5d6f014e3b/artifacts/c014elu5_image.png';
const FREEZE_DRYER = 'https://customer-assets.emergentagent.com/job_66080daa-dc30-4cf8-b53e-8e5d6f014e3b/artifacts/ldxk5a9u_HomeLyph_7_Tray_No_Pump.png';

/**
 * CustomerLoginPage - Branded dark split-screen login for the customer portal.
 */
const CustomerLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please enter your email and password');
      return;
    }
    try {
      setLoading(true);
      const user = await login({ email: formData.email, password: formData.password });
      if (user.role !== 'customer') {
        toast.error('This portal is for customers only. Please use the CRM login.');
        navigate('/login');
        return;
      }
      toast.success('Welcome back!');
      navigate('/my-portal');
    } catch (error) {
      const errorMessage = error.response?.data?.detail;
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#001828] font-['Manrope']">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-72 h-72 bg-[#00C0C0]/10 rounded-full blur-3xl" />
        <div className="relative w-full max-w-md">
          <div className="mb-8 text-center">
            <img src={CHINOOK_LOGO} alt="Chinook Manufacturing" className="h-14 mx-auto mb-6 object-contain" />
            <h1 className="text-2xl font-semibold text-white font-['Outfit'] tracking-tight">Customer Support Portal</h1>
            <p className="text-[#94A3B8] mt-1 text-sm">Sign in to manage your equipment &amp; support</p>
          </div>

          <div className="bg-[#003050]/60 backdrop-blur-xl border border-[#00C0C0]/20 rounded-2xl shadow-2xl p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#94A3B8]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#00C0C0]" />
                  <Input
                    id="email" type="email" placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="pl-10 bg-[#001828] border-[#00486D] text-white placeholder:text-slate-500 focus-visible:ring-[#00C0C0]"
                    data-testid="customer-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#94A3B8]">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#00C0C0]" />
                  <Input
                    id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="pl-10 pr-10 bg-[#001828] border-[#00486D] text-white placeholder:text-slate-500 focus-visible:ring-[#00C0C0]"
                    data-testid="customer-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#00C0C0] transition-colors"
                    tabIndex={-1} data-testid="toggle-password-visibility">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading}
                className="w-full bg-[#00C0C0] text-[#001828] font-semibold hover:bg-[#00D0E0] rounded-full"
                data-testid="customer-login-btn">
                {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in...</>) : (<>Sign In<ArrowRight className="h-4 w-4 ml-2" /></>)}
              </Button>

              <div className="text-center">
                <Link to="/forgot-password?portal=customer" className="text-sm text-[#00C0C0] hover:text-[#00D0E0] font-medium" data-testid="customer-forgot-password-link">
                  Forgot your password?
                </Link>
              </div>
            </form>

            <div className="mt-5 pt-5 border-t border-[#00486D] text-center space-y-3">
              <p className="text-sm text-[#94A3B8]">
                Don&apos;t have an account?{' '}
                <Link to="/customer-register" className="text-[#00C0C0] hover:text-[#00D0E0] font-semibold">Register here</Link>
              </p>
              <div className="flex justify-center">
                <InstallAppButton variant="outline" className="border-[#00C0C0] text-[#00C0C0] hover:bg-[#00C0C0]/10 rounded-full" />
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-[#94A3B8]">
            <Shield className="h-4 w-4 text-[#00C0C0]" /> Secure customer portal
          </div>
          <div className="mt-3 text-center">
            <Link to="/login" className="text-xs text-[#94A3B8] hover:text-[#00C0C0]">Are you a staff member? Go to CRM Login</Link>
          </div>
        </div>
      </div>

      {/* Right: product hero (desktop) */}
      <div className="hidden lg:flex relative items-center justify-center overflow-hidden bg-gradient-to-br from-[#002036] to-[#001019]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00C0C0]/15 rounded-full blur-3xl" />
        <div className="relative z-10 text-center px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C0C0] mb-4">Next Generation Freeze Drying</p>
          <img src={FREEZE_DRYER} alt="Chinook freeze dryer" className="max-h-[52vh] w-auto mx-auto drop-shadow-2xl" />
          <h2 className="text-3xl font-semibold text-white font-['Outfit'] mt-6 tracking-tight">Support for your HomeLyph &amp; D-Freeze</h2>
          <p className="text-[#94A3B8] mt-3 max-w-md mx-auto">Register products, file warranty claims, chat with support, book meetings and more — all in one place.</p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#00C0C0]/10 border border-[#00C0C0]/30 px-4 py-2 text-sm text-[#00D0E0]">
            <Smartphone className="h-4 w-4" /> Install the app on your phone for quick access
          </div>
        </div>
      </div>
      <InstallBanner />
    </div>
  );
};

export default CustomerLoginPage;
